import { Link } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import type { Location } from "@/data/locations";
import { getProvinsi, findKotaBySlug } from "@/data/locations";

type Variant = "sholat" | "imsak" | "buka-puasa";

const ROOT_LABEL: Record<Variant, string> = {
  sholat: "Jadwal Sholat",
  imsak: "Jadwal Imsakiyah",
  "buka-puasa": "Jadwal Buka Puasa",
};

function rootPath(v: Variant) {
  return v === "buka-puasa" ? "/jadwal-buka-puasa/" : `/jadwal-${v}/`;
}

/**
 * Breadcrumb for /jadwal-sholat, /jadwal-imsak, /jadwal-buka-puasa pages.
 * Renders semantic <ol> + JSON-LD BreadcrumbList for SEO. Always provides
 * a clear path: Beranda → Jadwal X → Provinsi → Kota → Kecamatan.
 */
export function PrayerBreadcrumb({ variant, location }: { variant: Variant; location?: Location | null }) {
  const root = rootPath(variant);
  const items: Array<{ label: string; href?: string }> = [
    { label: "Beranda", href: "/" },
    { label: ROOT_LABEL[variant], href: location ? root : undefined },
  ];

  if (location) {
    const prov = getProvinsi(location.provinceSlug);
    if (prov && location.kind !== "provinsi") {
      items.push({ label: prov.name, href: `${root}${prov.slug}/` });
    }
    if (location.kind === "kecamatan" && location.parentSlug) {
      const kota = findKotaBySlug(location.parentSlug);
      items.push({
        label: kota?.name ?? location.parentSlug,
        href: `${root}${location.parentSlug}/`,
      });
    }
    items.push({ label: location.name });
  }

  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label,
      ...(it.href ? { item: `https://hari.co.id${it.href}` } : {}),
    })),
  };

  return (
    <nav aria-label="Breadcrumb" className="mb-3 text-sm">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <ol className="flex flex-wrap items-center gap-1 text-foreground/75">
        {items.map((it, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${it.label}-${i}`} className="inline-flex items-center gap-1">
              {i === 0 && <Home className="h-3.5 w-3.5" aria-hidden />}
              {it.href && !last ? (
                <Link to={it.href as never} className="rounded px-1 hover:text-prayer hover:underline">
                  {it.label}
                </Link>
              ) : (
                <span aria-current={last ? "page" : undefined} className={last ? "font-semibold text-foreground" : "px-1"}>
                  {it.label}
                </span>
              )}
              {!last && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}